import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export type LoyaltyCartItem = {
    key: string; // "product-12" | "service-34"
    catalog_item_id: number;
    product_id?: number | null;
    service_id?: number | null;
    quantity: number;
    unit_loyalty_price: number;
    name: string;
    image_url?: string | null;
    started_at?: string;
    ended_at?: string;
    comment?: string;
    requires_note?: boolean;
};

type LoyaltyCartBase = Omit<LoyaltyCartItem, "key" | "quantity">;

type LoyaltyCartContextType = {
    items: LoyaltyCartItem[];
    itemCount: number;
    totalPoints: number;
    setItemQuantity: (base: LoyaltyCartBase, qty: number) => void;
    removeItem: (key: string) => void;
    clearCart: () => void;
};

const DEFAULT_STORAGE_KEY = "loyaltyCart:v1";

export const LoyaltyCartContext = createContext<LoyaltyCartContextType | undefined>(undefined);

type ProviderProps = {
    children: React.ReactNode;
    /** Pour “namespacer” par user/facility : ex. `loyaltyCart:${facilityId}:${userId}` */
    storageKey?: string;
};

export function LoyaltyCartProvider({ children, storageKey = DEFAULT_STORAGE_KEY }: ProviderProps) {
    const [items, setItems] = useState<LoyaltyCartItem[]>([]);

    // HYDRATE depuis localStorage
    useEffect(() => {
        try {
            const raw = typeof window !== "undefined" ? localStorage.getItem(storageKey) : null;
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) setItems(parsed);
        } catch (e) {
            console.warn("LoyaltyCart hydrate failed:", e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey]);

    // PERSIST dans localStorage
    useEffect(() => {
        try {
            if (typeof window !== "undefined") {
                localStorage.setItem(storageKey, JSON.stringify(items));
            }
        } catch (e) {
            console.warn("LoyaltyCart persist failed:", e);
        }
    }, [items, storageKey]);

    const setItemQuantity: LoyaltyCartContextType["setItemQuantity"] = (base, qty) => {
        // petit garde-fou si jamais tout est null/undefined
        const key =
            base.product_id != null
                ? `product-${base.product_id}`
                : `service-${base.service_id}`;

        setItems(prev => {
            if (!qty || qty <= 0) return prev.filter(i => i.key !== key);
            const existing = prev.find(i => i.key === key);
            const next: LoyaltyCartItem = { key, ...base, quantity: qty };
            return existing ? prev.map(i => (i.key === key ? next : i)) : [...prev, next];
        });
    };

    const removeItem = (key: string) => setItems(prev => prev.filter(i => i.key !== key));
    const clearCart = () => setItems([]);

    const { itemCount, totalPoints } = useMemo(() => {
        const c = items.reduce((acc, it) => acc + (it.quantity || 0), 0);
        const p = items.reduce((acc, it) => acc + (it.quantity * (it.unit_loyalty_price || 0)), 0);
        return { itemCount: c, totalPoints: p };
    }, [items]);

    const value: LoyaltyCartContextType = { items, itemCount, totalPoints, setItemQuantity, removeItem, clearCart };

    return <LoyaltyCartContext.Provider value={value}>{children}</LoyaltyCartContext.Provider>;
}

export function useLoyaltyCart() {
    const ctx = useContext(LoyaltyCartContext);
    if (!ctx) throw new Error("useLoyaltyCart must be used within LoyaltyCartProvider");
    return ctx;
}
